"use client";

/**
 * Shopping 페이지 클라이언트 컨텐츠
 *
 * TanStack Query 훅 + Zustand UI 상태 + Auth 상태를 조합
 * Wave 2: 검색 UI 통합 + EmptyView + 실제 API 응답 구조 반영
 */
import { useRouter, useSearchParams } from "next/navigation";

import type { GetProductsParams } from "@/shared/api/services/productService";
import { findCategoryById, findParentCategory } from "@/shared/constants/categories";
import { usePagination } from "@/shared/hooks/usePagination";

import { EmptyView } from "@/features/shopping/components/EmptyView";
import { FilterSheetTrigger, FilterSidebar } from "@/features/shopping/components/FilterSidebar";
import { ProductList } from "@/features/shopping/components/ProductList";
import { ProductListSkeleton } from "@/features/shopping/components/ProductListSkeleton";
import { useProducts, useProductSearch } from "@/features/shopping/queries/useProducts";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

export function ShoppingContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const page = Number(searchParams.get("page") ?? "1");
  const keyword = searchParams.get("q") ?? "";

  const idCategoryRaw = searchParams.get("idCategory") ?? undefined;
  const orderRaw = searchParams.get("order") ?? searchParams.get("sort") ?? undefined;
  const filters = searchParams.get("filters") ?? undefined;

  // 신제품(146)은 실제 서브카테고리가 아니라 가상 마커. 레거시 백엔드 getProductByCategory는
  // launchYmdt 14일 윈도로 해석하는데 운영 데이터의 launchYmdt 미기입으로 빈 결과가 잦다.
  // 홈 신제품 위젯과 동일하게 idCategory 없이 order=latest 로 호출해 정합을 맞춘다.
  const isNewProductsView = idCategoryRaw === "146";
  const idCategory = isNewProductsView ? undefined : idCategoryRaw;
  const order = isNewProductsView ? (orderRaw ?? "latest") : orderRaw;

  const params: GetProductsParams = {
    idCategory,
    order,
    filters,
    page,
  };

  // 두 훅 모두 무조건 호출 (React Hooks 규칙)
  // useProductSearch는 enabled 플래그로 게이팅
  const productsQuery = useProducts(params);
  const searchQuery = useProductSearch(keyword, page);

  // 카테고리 제목 (부모 > 현재 형식) — UI 라벨은 신제품일 때도 유지하기 위해 raw 사용
  const idCategoryNum = Number(idCategoryRaw ?? "0");
  const season = productsQuery.data?.data?.products?.season;
  let categoryTitle: string | null = null;
  if (idCategoryNum > 0) {
    const parent = findParentCategory(idCategoryNum);
    const current = findCategoryById(idCategoryNum);
    if (parent && current) {
      const isParent = parent.id === idCategoryNum;
      const hasSubs = (parent.subcategories?.length ?? 0) > 0;
      categoryTitle = isParent
        ? hasSubs
          ? `${parent.label} > 전체`
          : parent.label
        : `${parent.label} > ${current.label}`;
    }
  }

  if (idCategoryNum === 14 && season) {
    categoryTitle = `시즌상품 > ${season.label}`;
  }

  // 활성 결과 선택
  const isSearchMode = keyword.length > 0;
  const activeQuery = isSearchMode ? searchQuery : productsQuery;
  const rawProducts = activeQuery.data?.data?.products?.list ?? [];
  const totalPages = activeQuery.data?.data?.products?.pagination?.total_page ?? 1;

  // 클라이언트 사이드 가격 필터 (백엔드 미지원 fallback)
  const filtered = filters
    ? rawProducts.filter((p) => {
        const match = filters.match(/^price:(\d+)-(\d+)$/);
        if (!match) return true;
        const min = Number(match[1]);
        const max = Number(match[2]);
        return p.goodsPrice >= min && p.goodsPrice <= max;
      })
    : rawProducts;

  // 클라이언트 사이드 정렬 fallback
  const products = (() => {
    switch (order) {
      case "price_asc":
        return [...filtered].sort((a, b) => a.goodsPrice - b.goodsPrice);
      case "price_desc":
        return [...filtered].sort((a, b) => b.goodsPrice - a.goodsPrice);
      case "latest":
        return [...filtered].sort((a, b) => b.id - a.id);
      default:
        return filtered;
    }
  })();

  const { navigateToPage, renderPageNumbers } = usePagination({
    page,
    totalPages,
    searchParams,
    router,
  });

  function handleResetFilters() {
    router.push("/shopping");
  }

  return (
    <div className="container mx-auto px-4 py-6 flex flex-col gap-6">
      {/* 메인 레이아웃: 사이드바 + 컨텐츠 */}
      <div className="flex gap-8">
        <FilterSidebar />

        {/* 컨텐츠 영역 */}
        <div className="flex-1 min-w-0 flex flex-col gap-6">
          {categoryTitle && !isSearchMode && (
            <h1 className="text-lg font-semibold text-foreground">{categoryTitle}</h1>
          )}

          {/* 모바일 필터 버튼 */}
          <FilterSheetTrigger />

          {activeQuery.isLoading ? (
            <ProductListSkeleton />
          ) : activeQuery.isError ? (
            <EmptyView variant="error" />
          ) : products.length === 0 ? (
            <EmptyView
              variant={isSearchMode ? "no-search-results" : "no-results"}
              keyword={isSearchMode ? keyword : undefined}
              onReset={handleResetFilters}
            />
          ) : (
            <>
              {/* 검색 결과 헤더 */}
              {isSearchMode && (
                <p className="text-sm text-muted-foreground">
                  &ldquo;{keyword}&rdquo; 검색 결과{" "}
                  {activeQuery.data?.data?.products?.pagination?.data_count ?? 0}건
                </p>
              )}

              <ProductList products={products} />

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <Pagination className="mt-8">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => navigateToPage(Math.max(1, page - 1))}
                        aria-disabled={page <= 1}
                        className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                    {renderPageNumbers()}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => navigateToPage(Math.min(totalPages, page + 1))}
                        aria-disabled={page >= totalPages}
                        className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
